/**
 * Sponsored map-pin style registry — admin / wizard side.
 *
 * Mirrors the mobile registry at `src/constants/pinStyles.ts`. The
 * `key` is what gets written to `local_business_listings.pin_style`,
 * which is also what the mobile clients read at render time, so any
 * change here MUST be made there too.
 *
 * Web side uses Lucide icons (already in the admin bundle); mobile
 * side uses Ionicons. The colour + label fields are identical so the
 * Live Preview marker matches what passengers + drivers will see.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Utensils, Coffee, Wine, Hotel, Umbrella, ShoppingBag, ShoppingCart,
  Cross, Stethoscope, Flower, Dumbbell, Landmark, CreditCard, Phone, Car, Fuel,
  Compass, Camera, Zap, Building2, GraduationCap, Music, Sparkles,
  Star,
} from 'lucide-react';

export type PinStyleKey =
  | 'restaurant'
  | 'cafe'
  | 'bar'
  | 'hotel'
  | 'beach'
  | 'store'
  | 'supermarket'
  | 'pharmacy'
  | 'medical'
  | 'spa'
  | 'gym'
  | 'bank'
  | 'atm'
  | 'telecom'
  | 'auto'
  | 'gas_station'
  | 'tour_operator'
  | 'attraction'
  | 'utility'
  | 'government'
  | 'education'
  | 'event_venue'
  | 'festival'
  | 'other';

export interface PinStyle {
  key: PinStyleKey;
  label: string;
  icon: LucideIcon;
  bg: string;
  fg: string;
  description: string;
  /** SVG-ready unicode-safe glyph name shown in the picker chip when
   *  Lucide isn't available (e.g. Leaflet inline HTML markers). */
  emoji: string;
}

export const PIN_STYLES: Record<PinStyleKey, PinStyle> = {
  restaurant:    { key: 'restaurant',    label: 'Restaurant',    icon: Utensils,       bg: '#ef4444', fg: '#ffffff', emoji: '🍽️',  description: 'Sit-down restaurants & local cuisine' },
  cafe:          { key: 'cafe',          label: 'Cafe',          icon: Coffee,         bg: '#b45309', fg: '#ffffff', emoji: '☕',  description: 'Coffee shops, bakeries, lunch counters' },
  bar:           { key: 'bar',           label: 'Bar / Nightlife', icon: Wine,         bg: '#7c3aed', fg: '#ffffff', emoji: '🍷',  description: 'Bars, lounges, rum shops, nightclubs' },
  hotel:         { key: 'hotel',         label: 'Hotel',         icon: Hotel,          bg: '#4f46e5', fg: '#ffffff', emoji: '🏨',  description: 'City hotels & guesthouses' },
  beach:         { key: 'beach',         label: 'Resort / Beach', icon: Umbrella,      bg: '#06b6d4', fg: '#ffffff', emoji: '🏖️',  description: 'Resorts, beachfront stays, eco lodges' },
  store:         { key: 'store',         label: 'Retail Store',  icon: ShoppingBag,    bg: '#ec4899', fg: '#ffffff', emoji: '🛍️',  description: 'Department stores, fashion, gifts' },
  supermarket:   { key: 'supermarket',   label: 'Supermarket',   icon: ShoppingCart,   bg: '#f59e0b', fg: '#ffffff', emoji: '🛒',  description: 'Grocery stores & supermarkets' },
  pharmacy:      { key: 'pharmacy',      label: 'Pharmacy',      icon: Cross,          bg: '#14b8a6', fg: '#ffffff', emoji: '⚕️',  description: 'Pharmacies & drug stores' },
  medical:       { key: 'medical',       label: 'Medical',       icon: Stethoscope,    bg: '#dc2626', fg: '#ffffff', emoji: '🏥',  description: 'Hospitals, clinics, doctors' },
  spa:           { key: 'spa',           label: 'Spa / Salon',   icon: Flower,         bg: '#f472b6', fg: '#ffffff', emoji: '💆',  description: 'Spas, salons, nail bars, barbershops' },
  gym:           { key: 'gym',           label: 'Gym / Fitness', icon: Dumbbell,       bg: '#ea580c', fg: '#ffffff', emoji: '🏋️',  description: 'Gyms, fitness studios, yoga, pilates' },
  bank:          { key: 'bank',          label: 'Bank',          icon: Landmark,       bg: '#1d4ed8', fg: '#ffffff', emoji: '🏦',  description: 'Banks & credit unions' },
  atm:           { key: 'atm',           label: 'ATM',           icon: CreditCard,     bg: '#2563eb', fg: '#ffffff', emoji: '💳',  description: 'Standalone ATMs & money services' },
  telecom:       { key: 'telecom',       label: 'Telecom',       icon: Phone,          bg: '#db2777', fg: '#ffffff', emoji: '📱',  description: 'Mobile carriers, internet, cable' },
  auto:          { key: 'auto',          label: 'Auto / Dealer', icon: Car,            bg: '#475569', fg: '#ffffff', emoji: '🚗',  description: 'Car dealers, garages, rentals' },
  gas_station:   { key: 'gas_station',   label: 'Gas Station',   icon: Fuel,           bg: '#f97316', fg: '#ffffff', emoji: '⛽',  description: 'Service stations & fuel stops' },
  tour_operator: { key: 'tour_operator', label: 'Tour Operator', icon: Compass,        bg: '#0ea5e9', fg: '#ffffff', emoji: '🧭',  description: 'Tour & adventure operators, dive shops' },
  attraction:    { key: 'attraction',    label: 'Attraction',    icon: Camera,         bg: '#14b8a6', fg: '#ffffff', emoji: '📸',  description: 'Sights, parks, waterfalls, beaches' },
  utility:       { key: 'utility',       label: 'Utility',       icon: Zap,            bg: '#eab308', fg: '#111827', emoji: '⚡',  description: 'Electricity, water, internet, ports' },
  government:    { key: 'government',    label: 'Government',    icon: Building2,      bg: '#6366f1', fg: '#ffffff', emoji: '🏛️',  description: 'Government offices, tourism boards' },
  education:     { key: 'education',     label: 'Education',     icon: GraduationCap,  bg: '#0891b2', fg: '#ffffff', emoji: '🎓',  description: 'Schools, colleges, universities' },
  event_venue:   { key: 'event_venue',   label: 'Event Venue',   icon: Music,          bg: '#c026d3', fg: '#ffffff', emoji: '🎵',  description: 'Concert halls, stadiums, event spaces' },
  festival:      { key: 'festival',      label: 'Festival',      icon: Sparkles,       bg: '#be185d', fg: '#ffffff', emoji: '✨',  description: 'Festival sites, carnival routes' },
  other:         { key: 'other',         label: 'Generic',       icon: Star,           bg: '#10b981', fg: '#ffffff', emoji: '⭐',  description: "Doesn't fit anywhere else" },
};

export const PIN_STYLE_KEYS = Object.keys(PIN_STYLES) as PinStyleKey[];

const CATEGORY_TO_PIN_STYLE: Record<string, PinStyleKey> = {
  restaurant: 'restaurant',
  cafe: 'cafe',
  bakery: 'cafe',
  bar: 'bar',
  nightclub: 'bar',
  distillery: 'bar',
  hotel: 'hotel',
  guesthouse: 'hotel',
  resort: 'beach',
  retail: 'store',
  boutique: 'store',
  supermarket: 'supermarket',
  convenience_store: 'supermarket',
  grocery: 'supermarket',
  pharmacy: 'pharmacy',
  medical: 'medical',
  spa: 'spa',
  salon: 'spa',
  gym: 'gym',
  fitness: 'gym',
  bank: 'bank',
  atm: 'atm',
  telecom: 'telecom',
  media: 'telecom',
  auto: 'auto',
  gas_station: 'gas_station',
  tour_operator: 'tour_operator',
  watersports: 'tour_operator',
  attraction: 'attraction',
  gallery: 'attraction',
  utility: 'utility',
  government: 'government',
  tourism_board: 'government',
  education: 'education',
  law_firm: 'other',
  funeral_home: 'other',
};

/** Resolves the registry entry for a given (pin_style, category) pair.
 *  Mirrors the mobile-side `resolvePinStyle` exactly. */
export function resolvePinStyle(
  pinStyle: string | null | undefined,
  category: string | null | undefined,
): PinStyle {
  if (pinStyle && pinStyle in PIN_STYLES) {
    return PIN_STYLES[pinStyle as PinStyleKey];
  }
  if (category) {
    const mapped = CATEGORY_TO_PIN_STYLE[category.toLowerCase()];
    if (mapped) return PIN_STYLES[mapped];
  }
  return PIN_STYLES.other;
}

/** SVG path for the Lucide icon, used for inline HTML markers (Leaflet
 *  divIcon HTML in the wizard preview can't render React components,
 *  so we ship the inline SVG path data once per icon). */
export const PIN_STYLE_SVG: Record<PinStyleKey, string> = {
  // Each path is the SVG `d` attribute for a 24×24 stroke icon. Kept
  // inline so the Leaflet marker doesn't need to fetch icon assets.
  restaurant:    'M3 2v7c0 1.1.9 2 2 2h2v8a2 2 0 0 0 4 0v-8h2a2 2 0 0 0 2-2V2 M7 2v7 M11 2v7 M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h1v6a2 2 0 0 0 4 0v-6h-2',
  cafe:          'M17 8h1a4 4 0 1 1 0 8h-1 M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z M6 2v2 M10 2v2 M14 2v2',
  bar:           'M8 22h8 M7 10h10 M12 15v7 M12 15a5 5 0 0 1-5-5V3h10v7a5 5 0 0 1-5 5Z',
  hotel:         'M2 4v16 M2 8h18a2 2 0 0 1 2 2v10 M2 17h20 M6 8v9',
  beach:         'M22 12a10.06 10.06 0 0 0-20 0Z M12 12v8a2 2 0 0 0 4 0 M12 2v1',
  store:         'M3 6h18l-2 13H5L3 6Z M3 6 5.5 2h13L21 6 M16 10a4 4 0 0 1-8 0',
  supermarket:   'M5 22a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z M19 22a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12',
  pharmacy:      'M11 2a2 2 0 0 0-2 2v5H4a2 2 0 0 0-2 2v2c0 1.1.9 2 2 2h5v5c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-2-2h-2z',
  medical:       'M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3 M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4 M18 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  spa:           'M12 7.5a4.5 4.5 0 1 1 4.5 4.5M12 7.5A4.5 4.5 0 1 0 7.5 12M12 7.5V9m-4.5 3a4.5 4.5 0 1 0 4.5 4.5M7.5 12H9m7.5 0a4.5 4.5 0 1 1-4.5 4.5M16.5 12H15m-3 4.5V15M3 3l3 3m12-3l-3 3m-9 12l3-3m9 3l-3-3',
  gym:           'M14.4 14.4 9.6 9.6 M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829zM21.5 21.5l-1.4-1.4 M3.9 3.9 2.5 2.5 M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z',
  bank:          'M3 22h18 M5 11l7-7 7 7 M6 21V11 M10 21v-6a2 2 0 0 1 4 0v6 M18 21V11',
  atm:           'M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z M2 10h20',
  telecom:       'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z',
  auto:          'M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2 M14 17H9 M5 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z M19 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
  gas_station:   'M3 22V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v18 M14 13h2a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2 2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5 M2 22h13 M9 6h3v4H9z',
  tour_operator: 'M16.24 7.76a6 6 0 0 1 0 8.49 6 6 0 0 1-8.48 0 6 6 0 0 1 0-8.49 6 6 0 0 1 8.48 0Z M12 22v-2 M12 4V2 M22 12h-2 M4 12H2 M19.07 19.07l-1.41-1.41 M6.34 6.34 4.93 4.93',
  attraction:    'M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z M12 18a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  utility:       'M13 2 3 14h9l-1 8 10-12h-9l1-8Z',
  government:    'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z M6 12H4a2 2 0 0 0-2 2v8h4 M18 9h2a2 2 0 0 1 2 2v11h-4 M10 6h4 M10 10h4 M10 14h4 M10 18h4',
  education:     'M22 10v6M2 10l10-5 10 5-10 5z M6 12v5c3 3 9 3 12 0v-5',
  event_venue:   'M9 18V5l12-2v13 M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  festival:      'm12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z M5 3v4 M19 17v4 M3 5h4 M17 19h4',
  other:         'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z',
};
